/**
 * Documento imprimible de la Declaración de Renta, régimen Pro PYME Transparente
 * (Art. 14 letra D) N° 8 LIR) — Recuadros N° 22 y 23. El año sale de data.anioTributario.
 *
 * El membrete, los antecedentes, los estilos, la nota legal y las firmas son los de
 * todos los PDF de renta (../common/pdfComun.js); aquí van solo los recuadros, la
 * asignación a los propietarios, el resultado del ejercicio y las notas de este régimen.
 */
const { fmt, fieldRow, subsectionHtml, sectionHtml, buildDocumento, abrirParaImprimir } = require('../common/pdfComun');
const { r22: r22Fn, r23: r23Fn } = require('./formulas');
const { LINEAS_INGRESOS, LINEAS_EGRESOS, CREDITOS_IDPC } = require('../../../../services/dj1947/calculo');

// -----------------------------------------------------------------------
// R22 – Base imponible a asignar a los propietarios (desde la DJ 1947)
// -----------------------------------------------------------------------
const buildR22 = (data) => {
  const linea = (signo) => ({ codigo, titulo }) => fieldRow(signo, codigo, titulo, fmt(r22Fn.linea(data, codigo)));
  const ingresos = subsectionHtml('Ingresos', [
    ...LINEAS_INGRESOS.map(linea('(+)')),
    fieldRow('(=)', '1610', 'Total de ingresos anuales', fmt(r22Fn.totalIngresos(data)), 'subtotal'),
  ]);
  const egresos = subsectionHtml('Egresos', [
    ...LINEAS_EGRESOS.map(linea('(-)')),
    fieldRow('(=)', '1629', 'Total de egresos anuales', fmt(r22Fn.totalEgresos(data)), 'subtotal'),
  ]);
  const resultado = `<div class="subsection">${fieldRow('(=)', '1630', 'Base imponible a asignar a propietarios que son contribuyentes de impuestos finales, o pérdida tributaria del ejercicio', fmt(r22Fn.baseImponible(data)), 'total')}</div>`;

  return sectionHtml(
    'I',
    'Recuadro N° 22 — Base Imponible',
    'Régimen de Transparencia Tributaria, Art. 14 letra D) N° 8 de la Ley sobre Impuesto a la Renta — según DJ 1947',
    ingresos + egresos + resultado,
    'section-continua'
  );
};

// -----------------------------------------------------------------------
// R23 – CPTS
// -----------------------------------------------------------------------
const buildR23 = (data) => {
  const rows = [
    fieldRow('(+)', '1580', 'CPT o CPTS positivo inicial',                                        fmt(r23Fn.cptInicialPositivo(data))),
    fieldRow('(-)', '1582', 'CPT o CPTS negativo inicial',                                        fmt(r23Fn.cptInicialNegativo(data))),
    fieldRow('(+)', '1573', 'Capital aportado empresas que inician actividades en el año comercial', fmt(r23Fn.capitalAportado(data))),
    fieldRow('(+)', '1574', 'Aumentos (efectivos) de capital del ejercicio',                      fmt(r23Fn.aumentosCapital(data))),
    fieldRow('(-)', '1575', 'Disminuciones (efectivas) de capital del ejercicio',                 fmt(r23Fn.disminucionesCapital(data))),
    fieldRow('(+)', '1712', 'Base imponible del ejercicio, asignable a los propietarios',         fmt(r23Fn.baseImponible(data))),
    fieldRow('(-)', '1713', 'Pérdida tributaria del ejercicio al 31 de diciembre',                fmt(r23Fn.perdida(data))),
    fieldRow('(+)', '1714', 'Pérdidas tributarias de ejercicios anteriores',                      fmt(r23Fn.perdidasAnteriores(data))),
    fieldRow('(-)', '1576', 'Remesas, retiros o dividendos repartidos en el ejercicio',           fmt(r23Fn.retiros(data))),
    fieldRow('(-)', '1715', 'Ingreso diferido imputado en el ejercicio',                          fmt(r23Fn.ingresoDiferido(data))),
    fieldRow('(-)', '1577', 'Partidas de gastos no aceptados',                                    fmt(r23Fn.gastosNoAceptados(data))),
    fieldRow('(-)', '1716', 'Crédito por activos fijos adquiridos en el ejercicio (art. 33 bis LIR)', fmt(r23Fn.credito33bis(data))),
    fieldRow('(-)', '1578', 'Crédito por IDPC, por participaciones en otras empresas',            fmt(r23Fn.creditoIDPC(data))),
    fieldRow('(+)', '1584', 'Otras partidas a agregar',                                           fmt(r23Fn.otrasAgregar(data))),
    fieldRow('(-)', '1585', 'Otras partidas a deducir',                                           fmt(r23Fn.otrasDeducir(data))),
    fieldRow('(=)', '1581', 'CPTS positivo final',                                                fmt(r23Fn.cptsPositivoFinal(data)), 'total'),
    fieldRow('(=)', '1583', 'CPTS negativo final',                                                fmt(r23Fn.cptsNegativoFinal(data))),
  ];
  return sectionHtml(
    'II',
    'Recuadro N° 23 — CPTS',
    'Capital propio tributario simplificado, Art. 14 letra D) N° 8 LIR',
    `<div class="subsection">${rows.join('')}</div>`,
    'section-continua'
  );
};

// -----------------------------------------------------------------------
// Asignación a los propietarios (DJ 1947)
// -----------------------------------------------------------------------
const buildPropietarios = (data, computed) => {
  const filas = computed.propietarios || [];
  const t = computed.totalesPropietarios || {};
  const creditosIdpc = (fila) => CREDITOS_IDPC.reduce((total, credito) => total + (fila[credito.campo] || 0), 0);
  const tabla = `
    <table class="data-table">
      <thead>
        <tr>
          <th class="td-label">Propietario</th>
          <th>%</th>
          <th>Base imponible</th>
          <th>Créditos IDPC</th>
          <th>Crédito 33 bis</th>
          <th>PPM</th>
          <th>Certificado</th>
        </tr>
      </thead>
      <tbody>
        ${filas.map((fila) => `
        <tr>
          <td class="td-label">${fila.nombre || ''} · RUT ${fila.rut}</td>
          <td>${Number(fila.porcentaje).toLocaleString('es-CL', { maximumFractionDigits: 4 })}</td>
          <td>${fmt(fila.base_imponible)}</td>
          <td>${fmt(creditosIdpc(fila))}</td>
          <td>${fmt(fila.credito_33_bis)}</td>
          <td>${fmt(fila.ppm)}</td>
          <td class="td-code">${fila.numero_certificado}</td>
        </tr>`).join('')}
        <tr class="row-total">
          <td class="td-label">Total (${t.casos || 0} propietarios)</td>
          <td></td>
          <td>${fmt(t.base_imponible)}</td>
          <td>${fmt(CREDITOS_IDPC.reduce((total, credito) => total + (t[credito.campo] || 0), 0))}</td>
          <td>${fmt(t.credito_33_bis)}</td>
          <td>${fmt(t.ppm)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>`;
  return sectionHtml(
    'III',
    'Asignación a los propietarios',
    'Base imponible, créditos y PPM asignados a cada propietario según la DJ 1947, que tributan con impuestos finales',
    tabla,
    'section-continua'
  );
};

// -----------------------------------------------------------------------
// Resultado del ejercicio
// -----------------------------------------------------------------------
const buildResultadoFinal = (data, computed) => {
  const rows = [
    fieldRow('',    '1712', 'Base imponible asignable a los propietarios',                  fmt(computed.r23?.baseImponible), 'total'),
    fieldRow('',    '',     'PPM puestos a disposición de los propietarios (DJ 1947, columna C14)', fmt(computed.liquidacion?.ppmPuestosADisposicion)),
    fieldRow('(=)', '305',  'RESULTADO LIQUIDACIÓN ANUAL IMPUESTO A LA RENTA',              fmt(computed.liquidacion?.resultado305), 'tax-result'),
  ];
  return sectionHtml(
    'IV',
    'Resultado del ejercicio',
    `La empresa queda liberada del Impuesto de Primera Categoría — año comercial ${data.anioComercial}`,
    `<div class="subsection">${rows.join('')}</div>`,
    'section-final'
  );
};

// -----------------------------------------------------------------------
// Notas relevantes para la presentación de abril
// -----------------------------------------------------------------------
const buildNotasDeclaracion = () => `
  <div class="notas-block">
    <div class="block-title">Notas relevantes para la presentación del Formulario 22</div>

    <div class="nota-item">
      <div class="nota-title">1. Tributación de los propietarios</div>
      <p>En el régimen de transparencia tributaria la empresa queda liberada del Impuesto de Primera Categoría. La base
      imponible determinada se grava con los impuestos finales de sus propietarios en el mismo ejercicio, en la proporción
      informada en la Declaración Jurada N° 1947, la que debe presentarse antes que esta declaración.</p>
    </div>

    <div class="nota-item">
      <div class="nota-title">2. Pagos provisionales mensuales</div>
      <p>Los pagos provisionales mensuales enterados por la empresa no se imputan en su propia declaración: se ponen a
      disposición de los propietarios en la Declaración Jurada N° 1947 y cada uno los rebaja de su Impuesto Global
      Complementario en su Formulario 22 (código 1645).</p>
    </div>

    <div class="nota-item">
      <div class="nota-title">3. Pérdida tributaria</div>
      <p>Si el resultado del ejercicio es una pérdida tributaria, esta no se asigna a los propietarios: se deduce como
      egreso del ejercicio siguiente, a su valor nominal (Circular SII N° 62 de 2020, apartado 2.4.1).</p>
    </div>
  </div>`;

// -----------------------------------------------------------------------
// Documento completo
// -----------------------------------------------------------------------
const buildRenta14d8Html = (data, computed, company = {}) => buildDocumento(company, {
  anioTributario: data.anioTributario,
  anioComercial: data.anioComercial,
  regimen: 'Pro PYME Transparente — Art. 14 D) N° 8 LIR',
  formulario: 'N° 22 — Recuadros N° 22 y 23',
  subtitulos: [
    'Determinación de la Base Imponible asignable a los propietarios',
    'Régimen de Transparencia Tributaria — Art. 14 letra D) N° 8 de la Ley sobre Impuesto a la Renta',
  ],
  secciones: [
    buildR22(data),
    buildR23(data),
    buildPropietarios(data, computed),
    buildResultadoFinal(data, computed),
  ].join(''),
  notas: buildNotasDeclaracion(),
  notaLegal: {
    queSeDetermina: 'La determinación de la base imponible asignable a los propietarios',
    articulo: 'artículo 14 letra D) N° 8',
  },
});

// Exportación vía impresión del navegador (igual que el módulo 14 D N°3)
const downloadRenta14d8Pdf = (data, computed, company) => abrirParaImprimir(buildRenta14d8Html(data, computed, company));

module.exports = { buildRenta14d8Html, downloadRenta14d8Pdf };
